// 効果音（SE）定義テーブル。WebAudio の Oscillator で合成する。
// 音色変更はこのファイルの値を編集するだけで反映される（データ駆動）。

export type SeId =
  | 'place-player'   // プレイヤー（黒=青）が石を配置
  | 'place-cpu'      // CPU（白=赤）が石を配置
  | 'reach'          // 4連達成（リーチ。自分・相手共通）
  | 'win'            // プレイヤー勝利
  | 'lose'           // プレイヤー敗北

export interface OscillatorDef {
  type: OscillatorType        // sine | square | sawtooth | triangle
  frequency: number           // 開始周波数 (Hz)
  endFrequency?: number       // 指定時は start→end へ exponential ramp
  volume: number              // 0..1（masterVolume と掛け合わせて最終音量）
  delay?: number              // 発音開始までのオフセット秒（和音・アルペジオ用）
  attack: number              // 立ち上がり秒
  decay: number               // 減衰秒
  sustain: number             // サステインレベル 0..1
  release: number             // リリース秒
}

export interface SeDefinition {
  masterVolume: number
  oscillators: OscillatorDef[]
}

export const SE_DEFINITIONS: Record<SeId, SeDefinition> = {
  // プレイヤーの配置音: 低め〜中音の「コン」
  'place-player': {
    masterVolume: 0.35,
    oscillators: [
      {
        type: 'triangle',
        frequency: 340,
        endFrequency: 240,
        volume: 1,
        attack: 0.005,
        decay: 0.08,
        sustain: 0,
        release: 0.06,
      },
    ],
  },

  // CPU の配置音: 中音の「トン」（プレイヤーと音色差を付ける）
  'place-cpu': {
    masterVolume: 0.3,
    oscillators: [
      {
        type: 'triangle',
        frequency: 460,
        endFrequency: 360,
        volume: 1,
        attack: 0.005,
        decay: 0.06,
        sustain: 0,
        release: 0.05,
      },
    ],
  },

  // 4連リーチ: 短い上昇音で緊張感
  reach: {
    masterVolume: 0.4,
    oscillators: [
      {
        type: 'square',
        frequency: 760,
        endFrequency: 1150,
        volume: 0.6,
        attack: 0.01,
        decay: 0.08,
        sustain: 0,
        release: 0.1,
      },
    ],
  },

  // 勝利: C5 → E5 → G5 の上行アルペジオ
  win: {
    masterVolume: 0.45,
    oscillators: [
      { type: 'triangle', frequency: 523, volume: 0.9, delay: 0.00, attack: 0.01, decay: 0.14, sustain: 0, release: 0.10 },
      { type: 'triangle', frequency: 659, volume: 0.9, delay: 0.12, attack: 0.01, decay: 0.14, sustain: 0, release: 0.10 },
      { type: 'triangle', frequency: 784, volume: 0.9, delay: 0.24, attack: 0.01, decay: 0.22, sustain: 0, release: 0.22 },
    ],
  },

  // 敗北: G4 → E4 → C4 の下行
  lose: {
    masterVolume: 0.4,
    oscillators: [
      { type: 'triangle', frequency: 392, volume: 0.9, delay: 0.00, attack: 0.01, decay: 0.16, sustain: 0, release: 0.10 },
      { type: 'triangle', frequency: 329, volume: 0.9, delay: 0.16, attack: 0.01, decay: 0.16, sustain: 0, release: 0.10 },
      { type: 'triangle', frequency: 261, volume: 0.9, delay: 0.32, attack: 0.01, decay: 0.26, sustain: 0, release: 0.25 },
    ],
  },
}

// 同一 SE の連続呼び出しを吸収する最小間隔（ミリ秒）。
// React StrictMode の二重呼び出しやダブルクリック対策。
export const SE_DEBOUNCE_MS = 30
